import React, { FC, useEffect, useState, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { debounce } from 'lodash-es';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  Table,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { ContentBlockHeader, Divider } from '@/components';
import TwoColumnGrid from './TwoColumnGrid';
import BrandSuggestions from '@/applications/CspPortal/Campaigns/components/BrandSelectionForm/BrandSuggestions';
import {
  brandController,
  campaignBuilderController,
  UseCase,
} from '@/apis/core';
import { SelectItem } from '@/utils/types';
import { ProgramRegistrationFormData, BrandUseCase } from './types';
import { useUseCaseController } from './hooks';

interface Props {
  onNext?: () => void;
  onCancel?: () => void;
  useCases: UseCase[];
}

// Move static variables outside to avoid recreation on every render
const TABLE_HEADERS = [
  { id: 'usecase', label: 'USE CASE', sortable: false },
  { id: 'fee', label: 'TCR MONTHLY FEE', sortable: false },
];

const StepUsecase: FC<Props> = ({ onNext, onCancel, useCases }) => {
  const { control, setValue, clearErrors, watch } =
    useFormContext<ProgramRegistrationFormData>();
  const {
    brandUid,
    setBrandUid,
    brandName,
    setBrandName,
    brandNameError,
    usecase,
    setUsecase,
  } = useUseCaseController(control);

  // Local UI states
  const [brandOptions, setBrandOptions] = useState<SelectItem[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [loadingUseCases, setLoadingUseCases] = useState(false);
  const [processedUseCases, setProcessedUseCases] = useState<BrandUseCase[]>(
    []
  );

  const cachedBrandUseCases = watch('ui.brandUseCases');

  // Memoize the debounced function to preserve its instance across re-renders
  const fetchBrandOptions = useMemo(
    () =>
      debounce(async (prefix: string) => {
        setLoadingBrands(true);
        try {
          const suggestions = await brandController.getBrandSuggestions({
            status: 'ACTIVE',
            prefix,
          });

          const options: SelectItem[] = [];
          suggestions.forEach((item) => {
            const uids = item.brandUid.split(',');
            uids.forEach((uid) => {
              options.push({
                label: item.brandName,
                value: uid,
              });
            });
          });
          setBrandOptions(options);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch brands', err);
        } finally {
          setLoadingBrands(false);
        }
      }, 300),
    []
  );

  // Cleanup pending debounce requests on component unmount
  useEffect(() => {
    return () => {
      fetchBrandOptions.cancel();
    };
  }, [fetchBrandOptions]);

  const resetBrandState = () => {
    setUsecase('');
    setValue('ui.selectedUseCaseMetadata', null);
    setValue('ui.mnoNetworks', []);
    setValue('ui.brandUseCases', []);
    setValue('ui.usecaseDisplayName', '');
  };

  const handleBrandNameChange = (val: string) => {
    clearErrors('usecase.brandName');

    // Update form state directly and invalidate current uid as it's not verified
    setBrandName(val);
    setBrandUid('');
    setShowSuggestions(!!val);

    if (!val) {
      resetBrandState();
    }

    if (val.trim().length >= 2) {
      fetchBrandOptions(val.trim());
    } else {
      // Cancel pending request if input length < 2 or cleared
      fetchBrandOptions.cancel();
      setBrandOptions([]);
    }
  };

  const handleSelectBrand = (option?: SelectItem) => {
    if (option) {
      setBrandUid(option.value);
      setBrandName(option.label);
    } else {
      setBrandUid('');
      setBrandName('');
    }
    resetBrandState();
    setShowSuggestions(false);
  };

  // Cross-reference use cases and brand metadata to calculate row accessibility
  useEffect(() => {
    if (!brandUid) {
      const initial = useCases.map((uc) => ({
        ...uc,
        disabled: true,
        mnoMetadata: null,
      }));
      setProcessedUseCases(initial);
      if (cachedBrandUseCases && cachedBrandUseCases.length > 0) {
        setValue('ui.brandUseCases', []);
      }
      return;
    }

    // Cache hit logic: Bypasses API load and loading transition when navigating backward
    if (cachedBrandUseCases && cachedBrandUseCases.length > 0) {
      setProcessedUseCases(cachedBrandUseCases);
      return;
    }

    const loadBrandUseCases = async () => {
      setLoadingUseCases(true);
      const metadataList =
        await campaignBuilderController.getCampaignBuilderMetadata(brandUid);

      const combined = useCases.map((uc) => {
        const meta = metadataList.find((m) => m.usecase === uc.id);

        let isDisabled = true;
        let mnoMetadata = null;

        if (meta) {
          mnoMetadata = meta.mnoMetadata;
          const mnos = Object.values(meta.mnoMetadata || {});
          const hasQualifiedMno = mnos.some((mno) => mno.qualify);
          isDisabled = !hasQualifiedMno;
        }

        return {
          ...uc,
          disabled: isDisabled,
          mnoMetadata,
        };
      });
      setProcessedUseCases(combined);
      setValue('ui.brandUseCases', combined);
      setLoadingUseCases(false);
    };

    loadBrandUseCases();
  }, [brandUid, useCases, cachedBrandUseCases, setValue]);

  // Handle the specific use case selection click
  const handleSelectUseCase = (uc: BrandUseCase) => {
    if (!uc.disabled && uc.mnoMetadata) {
      setUsecase(uc.id);
      setValue('ui.usecaseDisplayName', uc.displayName);
      setValue('ui.selectedUseCaseMetadata', uc.mnoMetadata);

      const networks = Object.entries(uc.mnoMetadata).map(([id, m]) => ({
        id,
        name: m.mno,
        disabled: !m.qualify,
      }));
      setValue('ui.mnoNetworks', networks);
      // Set networks selected by default
      const selectedMnoIds = networks
        .filter((m) => !m.disabled)
        .map((m) => m.id);
      setValue('routing.mnoIds', selectedMnoIds);
    }
  };

  const tableData = React.useMemo(() => {
    return processedUseCases.map((uc) => {
      const isSelected = usecase === uc.id;
      const isRowDisabled = uc.disabled;

      const usecaseCell = (
        <Flex
          onClick={() => handleSelectUseCase(uc)}
          sx={{
            alignItems: 'center',
            gap: '8px',
            cursor: isRowDisabled ? 'default' : 'pointer',
          }}
          data-testid={`usecaseCell-${uc.id}`}
        >
          <FontAwesomeIcon
            icon={isSelected ? faCircleCheck : faCircle}
            color={isRowDisabled ? '#bcc1c3' : '#333E41'}
            style={{ fontSize: '16px' }}
          />
          <Box
            sx={{
              fontSize: '12px',
              color: isRowDisabled ? '#bcc1c3' : '#4D5659',
            }}
          >
            {uc.displayName}
          </Box>
        </Flex>
      );

      const feeCell = (
        <Box
          sx={{
            fontSize: '12px',
            color: isRowDisabled ? '#bcc1c3' : '#4D5659',
          }}
        >
          €{uc.monthlyFee ?? 0}.-
        </Box>
      );

      return {
        rawUsecase: uc,
        usecase: usecaseCell,
        fee: feeCell,
      };
    });
  }, [processedUseCases, usecase]);

  return (
    <Flex
      sx={{ flexDirection: 'column', gap: 'xl', width: '100%' }}
      data-testid="stepUsecase"
    >
      <ContentBlockHeader
        title="Register Messaging Program"
        subTitle="Step 1 of 3 — Select a brand, available channel, and use case."
        sx={{ mb: 0 }}
      />
      <Divider />

      <TwoColumnGrid>
        {/* Verified Brand Dropdown */}
        <Box sx={{ position: 'relative', width: '100%' }}>
          <TextInput
            label="Verified Brand"
            value={brandName}
            onChange={handleBrandNameChange}
            placeholder="Search and select a brand"
            data-testid="brandSearchInput"
            error={!!brandNameError}
            note={brandNameError?.message}
          />
          {showSuggestions && brandName && (
            <BrandSuggestions
              loading={loadingBrands}
              text={brandName}
              options={brandOptions}
              onSelect={handleSelectBrand}
              onClickAway={() => handleSelectBrand()}
            />
          )}
        </Box>
        <Box />

        {/* Channel (Read-only Dropdown) */}
        <TextInput
          label="Channel"
          value="SMS"
          readOnly
          note="Only SMS is currently supported"
          data-testid="channelInput"
        />
      </TwoColumnGrid>

      {/* Use Cases Table */}
      <Box sx={{ width: '100%', mt: 2 }}>
        <Table
          loading={loadingUseCases}
          headRows={TABLE_HEADERS}
          data={tableData}
          emptyState="No available use cases found."
          emptyBlockHeight="120px"
          disableHover
          testId="usecasesTable"
        />
      </Box>

      {/* Navigation Buttons */}
      <Divider sx={{ mt: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 16, mt: 4 }}>
        <Button
          type="button"
          shape="square"
          variant="outline"
          color="secondary"
          size="lg"
          onClick={onCancel}
          data-testid="exitBtn"
        >
          Cancel
        </Button>
        <Button
          type="button"
          shape="square"
          color="primary"
          size="lg"
          disabled={!brandUid || !usecase}
          onClick={onNext}
          data-testid="nextStepBtn"
        >
          Next: Routing & Connectivity
        </Button>
      </Box>
    </Flex>
  );
};

export default StepUsecase;
